/* eslint-disable no-undefined */
/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
/* eslint-disable max-statements */
import { useEffect, useState, useCallback, useMemo } from "react";
import { gql } from "graphql-tag";
import { useMutation } from "@apollo/client";
import { useSnackbar } from "components/haven-design/snackbar";
import logger from "services/logger";
import { apolloClient } from "data/graphql";
import { refetchHandles } from "data";
import { Action } from "pages/dashboard/interests-profile/components/reason-modal";
import {
  ChangeInterestStage,
  ChangeInterestStageVariables,
  createActivityContainer,
  createActivityContainerVariables,
  QueryInterestProfile_interest as InterestType,
  logCall as logCallType,
  logCallVariables,
  SendMessagesProfile,
  SendMessagesProfileVariables,
} from "types/gql-types";
import { CREATE_ACTIVITY, SEND_MESSAGE, LOG_CALL } from "../interests-activities/queries";
import {
  ActionKey,
  actionKeys,
  tabActions,
  getSwitchesForFocusedAction,
  buildLogCallNote,
  hasPropertyUseCases,
  validateEmailValues,
  validateSmsValues,
  validateLetterValues,
  toHtmlBodies,
} from "./helpers";
import { AddNoteStateValues } from "./note-form";
import { LogCallStateValues } from "./log-call-form";
import { SendMessageStateValues } from "./message-form";
import { ChangeStatusStateValues } from "./change-status-form";

export type StateValues =
  LogCallStateValues |
  SendMessageStateValues |
  AddNoteStateValues |
  Partial<ChangeStatusStateValues>;

const MUTATION_CHANGE_STATUS = gql`
  mutation ChangeInterestStage(
    $interest: ID!,
    $status: String!,
    $reason: String
    $actionDate: String,
    $unitNumber: String
  ) {
    changeInterestStatus(
      interestId: $interest,
      status: $status,
      reason: $reason
      actionDate: $actionDate
      unitNumber: $unitNumber
    )
  }
`;

type Switches = Record<ActionKey, boolean>;
type ChannelErrors = { form: string; email: string; sms: string; letter: string };
type SendMessageFieldErrors = { emailSubject: string; smsUseCase: string };
type FieldErrors = {
  sendMessage: SendMessageFieldErrors;
  logCall: { outcome: string };
  addNote: { note: string };
  changeStatus: { status: string; reason: string };
};
type ErrorMsgs = {
  sendMessage: ChannelErrors;
  logCall: string;
  addNote: string;
  changeStatus: string;
};
type FormValues = {
  changeStatus: ChangeStatusStateValues;
  addNote: AddNoteStateValues;
  logCall: LogCallStateValues;
  sendMessage: SendMessageStateValues;
};

const initialStatusState: ChangeStatusStateValues = { actionDate: null, reason: "", status: "", unitNumber: null };
const initialNoteState: AddNoteStateValues = { note: "" };
const initialLogCallState: LogCallStateValues = { reason: "", outcome: "", note: "" };
const initialMessageState: SendMessageStateValues = {
  emailEnabled: true,
  emailBody: { ops: [] } as unknown as SendMessageStateValues["emailBody"],
  emailSubject: "",
  emailBccAddress: "",
  emailRespondToEnabled: false,
  letterEnabled: true,
  letterBody: { ops: [] } as unknown as SendMessageStateValues["letterBody"],
  smsEnabled: false,
  smsBody: { ops: [] } as unknown as SendMessageStateValues["smsBody"],
  smsPropertyUseCaseId: null,
};

const mutationRefetchQueries = ["activityFeed", "getActivityFeed", "QueryInterestProfile"];
const emptySwitches: Switches = {
  logCall: false,
  addNote: false,
  sendMessage: false,
  changeStatus: false,
};
const newEmptyChannelErrors = (): ChannelErrors => ({ form: "", email: "", sms: "", letter: "" });
const newEmptyFieldErrors = (): FieldErrors => ({
  sendMessage: { emailSubject: "", smsUseCase: "" },
  logCall: { outcome: "" },
  addNote: { note: "" },
  changeStatus: { status: "", reason: "" },
});
const isBlank = (value: string | null | undefined): boolean => !value || value.trim().length < 1;

type BatchActionsControllerReturn = {
  activeAction: Action;
  switches: Switches;
  tabErrors: Switches;
  tabErrorTooltips: Record<ActionKey, string>;
  errorMsgs: ErrorMsgs;
  fieldErrors: FieldErrors;
  disableSubmitControls: boolean;
  changeStatusState: ChangeStatusStateValues;
  resetAllState: () => void;
  onSwitchToggle: (key: ActionKey) => void;
  onTabClick: (key: ActionKey) => void;
  onSwitchChange: (values: StateValues, key: ActionKey) => void;
  submitAllActions: () => void;
};

export const useBatchActionsController = (
  focusedAction: Action | undefined,
  setBatchActionsModalFocusedAction: (action: Action | undefined) => void,
  interestListID: string,
  interest: InterestType,
): BatchActionsControllerReturn => {
  const interestID = interest.id;
  const { openSnackbar } = useSnackbar();

  const [changeStage] = useMutation<ChangeInterestStage, ChangeInterestStageVariables>(
    MUTATION_CHANGE_STATUS,
    {
      client: apolloClient,
      refetchQueries: mutationRefetchQueries,
      awaitRefetchQueries: true,
    },
  );

  const [createActivity] = useMutation<createActivityContainer, createActivityContainerVariables>(
    CREATE_ACTIVITY,
    {
      client: apolloClient,
      refetchQueries: mutationRefetchQueries,
      awaitRefetchQueries: true,
      variables: { data: {} },
    },
  );

  const [logCall] = useMutation<logCallType, logCallVariables>(LOG_CALL, {
    refetchQueries: mutationRefetchQueries,
    awaitRefetchQueries: true,
    client: apolloClient,
  });

  const [sendTemplateMessages] = useMutation<SendMessagesProfile, SendMessagesProfileVariables>(
    SEND_MESSAGE,
    {
      refetchQueries: mutationRefetchQueries,
      awaitRefetchQueries: true,
      client: apolloClient,
    },
  );

  const [activeAction, setActiveAction] = useState(Action.LogCall);
  const [disableSubmitControls, setDisableSubmitControls] =
    useState<boolean>(false);
  const [switches, setSwitches] = useState<Switches>(emptySwitches);
  const [formValues, setFormValues] = useState<FormValues>({
    changeStatus: initialStatusState,
    addNote: initialNoteState,
    logCall: initialLogCallState,
    sendMessage: initialMessageState,
  });
  const [tabErrors, setTabErrors] = useState<Switches>({
    sendMessage: false,
    logCall: false,
    addNote: false,
    changeStatus: false,
  });
  const [errorMsgs, setErrorMsgs] = useState<ErrorMsgs>({
    sendMessage: newEmptyChannelErrors(),
    logCall: "",
    addNote: "",
    changeStatus: "",
  });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>(newEmptyFieldErrors());

  const resetErrors = () => {
    setTabErrors({
      sendMessage: false,
      logCall: false,
      addNote: false,
      changeStatus: false,
    });
    setErrorMsgs({
      sendMessage: newEmptyChannelErrors(),
      logCall: "",
      addNote: "",
      changeStatus: "",
    });
    setFieldErrors(newEmptyFieldErrors());
  };
  const resetForms = () => setFormValues({
    changeStatus: initialStatusState,
    addNote: initialNoteState,
    logCall: initialLogCallState,
    sendMessage: initialMessageState,
  });
  const resetAllState = () => {
    setBatchActionsModalFocusedAction(undefined);
    setSwitches(emptySwitches);
    setDisableSubmitControls(false);
    resetForms();
    resetErrors();
  };

  const onChangeFocusedAction = () => {
    if (!focusedAction) return;
    setActiveAction(focusedAction);
    setSwitches(getSwitchesForFocusedAction(focusedAction));
    resetForms();
    resetErrors();
    setBatchActionsModalFocusedAction(focusedAction);
  };
  useEffect(onChangeFocusedAction, [focusedAction]);

  const validateLogCallForm = (
    values: LogCallStateValues = formValues.logCall,
    isEnabled: boolean = switches.logCall,
  ): boolean => {
    const outcomeError = isEnabled && isBlank(values.outcome) ? "Outcome is required." : "";
    const errorMsg = outcomeError;
    setErrorMsgs((prev) => ({ ...prev, logCall: errorMsg }));
    setTabErrors((prev) => ({ ...prev, logCall: Boolean(errorMsg) }));
    setFieldErrors((prev) => ({ ...prev, logCall: { outcome: outcomeError } }));
    return Boolean(errorMsg);
  };

  const mutationWithErrorHandling = <T>(
    request: Promise<T>,
    userMessage: string,
  ): Promise<T> => request.catch((err) => {
    logger.sendError(err);
    throw new Error(userMessage);
  });

  const submitLogCall = () => {
    const noteText = buildLogCallNote(
      formValues.logCall.reason,
      formValues.logCall.outcome,
      formValues.logCall.note,
    );
    return mutationWithErrorHandling(logCall({
      variables: {
        interestId: interestID,
        reason: formValues.logCall.reason,
        outcome: formValues.logCall.outcome,
        note: noteText,
      },
    }), "Error logging call. Please contact support@havenconnect.com for help.");
  };

  const validateAddNoteForm = (
    values: AddNoteStateValues = formValues.addNote,
    isEnabled: boolean = switches.addNote,
  ): boolean => {
    const noteError = isEnabled && isBlank(values.note) ? "Note message is required." : "";
    const errorMsg = noteError;
    setErrorMsgs((prev) => ({ ...prev, addNote: errorMsg }));
    setTabErrors((prev) => ({ ...prev, addNote: Boolean(errorMsg) }));
    setFieldErrors((prev) => ({ ...prev, addNote: { note: noteError } }));
    return Boolean(errorMsg);
  };

  const validateSendMsgForm = (
    values: SendMessageStateValues = formValues.sendMessage,
    isEnabled: boolean = switches.sendMessage,
  ): boolean => {
    if (!isEnabled) {
      setErrorMsgs((prev) => ({ ...prev, sendMessage: newEmptyChannelErrors() }));
      setTabErrors((prev) => ({ ...prev, sendMessage: false }));
      setFieldErrors((prev) => ({ ...prev, sendMessage: { emailSubject: "", smsUseCase: "" } }));
      return false;
    }
    const useCases = hasPropertyUseCases(interest);
    const noChannelsEnabled = !values.emailEnabled && !values.smsEnabled && !values.letterEnabled;
    const errorMsgForm = noChannelsEnabled ? "At least one channel must be enabled." : "";
    const emailSubjectError =
      values.emailEnabled && isBlank(values.emailSubject) ? "Email subject is required." : "";
    const smsUseCaseError =
      values.smsEnabled && useCases && values.smsPropertyUseCaseId === null ? "Use case is required." : "";
    const errorMsgEmail = validateEmailValues(values);
    const errorMsgSms = validateSmsValues(values);
    const errorMsgLetter = validateLetterValues(values);
    const channelErrors = {
      form: errorMsgForm,
      email: errorMsgEmail,
      sms: errorMsgSms,
      letter: errorMsgLetter,
    };
    const hasFailedFormValidation = Object.values(channelErrors).some(Boolean);
    setErrorMsgs((prev) => ({
      ...prev,
      sendMessage: channelErrors,
    }));
    setTabErrors((prev) => ({ ...prev, sendMessage: hasFailedFormValidation }));
    setFieldErrors((prev) => ({
      ...prev,
      sendMessage: { emailSubject: emailSubjectError, smsUseCase: smsUseCaseError },
    }));
    return hasFailedFormValidation;
  };

  const submitAddNote = () =>
    mutationWithErrorHandling(createActivity({
      variables: {
        data: {
          resourceId: interestID,
          title: "Note",
          type: "note",
          resourceTable: "interests",
          note: formValues.addNote.note || null,
        },
      },
    }), "Error adding note. Please contact support@havenconnect.com for help.");

  const submitSendMsg = () => {
    const bodies = toHtmlBodies(formValues.sendMessage);
    return mutationWithErrorHandling(sendTemplateMessages({
      variables: {
        interestListIds: [interestListID],
        interestIds: [interestID],
        elasticQuery: null,
        emailEnabled: formValues.sendMessage.emailEnabled,
        emailBody: bodies.email,
        emailSubject: formValues.sendMessage.emailSubject,
        emailBccAddress: formValues.sendMessage.emailBccAddress || null,
        emailRespondToEnabled: formValues.sendMessage.emailRespondToEnabled,
        letterEnabled: formValues.sendMessage.letterEnabled,
        letterBody: bodies.letter,
        smsEnabled: formValues.sendMessage.smsEnabled,
        smsBody: bodies.sms,
        smsPropertyUseCaseId: formValues.sendMessage.smsPropertyUseCaseId,
      },
    }), "Error sending message. Please don't retry in case part of the batch has been sent. Contact support@havenconnect.com");
  };

  const validateChangeStatusForm = (
    values: ChangeStatusStateValues = formValues.changeStatus,
    isEnabled: boolean = switches.changeStatus,
  ): boolean => {
    if (!isEnabled) {
      setErrorMsgs((prev) => ({ ...prev, changeStatus: "" }));
      setTabErrors((prev) => ({ ...prev, changeStatus: false }));
      setFieldErrors((prev) => ({ ...prev, changeStatus: { status: "", reason: "" } }));
      return false;
    }
    const statusError = isBlank(values.status) ? "Outcome is required." : "";
    const reasonRequired =
      values.status !== "Moved In" &&
      !isBlank(values.status) &&
      isBlank(values.reason);
    const reasonError = reasonRequired ? "Note message is required." : "";
    const errorMsg = [statusError, reasonError].filter(Boolean).join(" ");
    setErrorMsgs((prev) => ({ ...prev, changeStatus: errorMsg }));
    setTabErrors((prev) => ({ ...prev, changeStatus: Boolean(errorMsg) }));
    setFieldErrors((prev) => ({
      ...prev,
      changeStatus: { status: statusError, reason: reasonError },
    }));
    return Boolean(errorMsg);
  };

  const submitChangeStatus = () =>
    mutationWithErrorHandling(changeStage({
      variables: {
        interest: interestID,
        unitNumber: formValues.changeStatus.unitNumber,
        actionDate: formValues.changeStatus.actionDate,
        status: formValues.changeStatus.status,
        reason: formValues.changeStatus.reason || null,
      },
    }), "Error changing status. Please contact support@havenconnect.com for help.");

  const validateTabByKey = (
    key: ActionKey,
    values: FormValues = formValues,
    isEnabled: boolean = switches[key],
  ): boolean => {
    switch (key) {
      case "sendMessage":
        return validateSendMsgForm(values.sendMessage, isEnabled);
      case "addNote":
        return validateAddNoteForm(values.addNote, isEnabled);
      case "changeStatus":
        return validateChangeStatusForm(values.changeStatus, isEnabled);
      default:
        return validateLogCallForm(values.logCall, isEnabled);
    }
  };

  const submitTabByKey = (key: ActionKey): Promise<unknown> => {
    switch (key) {
      case "changeStatus":
        return submitChangeStatus();
      case "sendMessage":
        return submitSendMsg();
      case "addNote":
        return submitAddNote();
      default:
        return submitLogCall();
    }
  };

  const hasFieldErrorsByKey = (key: ActionKey): boolean => {
    switch (key) {
      case "sendMessage":
        return Object.values(fieldErrors.sendMessage).some(Boolean);
      case "addNote":
        return Boolean(fieldErrors.addNote.note);
      case "changeStatus":
        return Boolean(fieldErrors.changeStatus.status || fieldErrors.changeStatus.reason);
      default:
        return Boolean(fieldErrors.logCall.outcome);
    }
  };

  const hasMessagesByKey = (key: ActionKey): boolean => {
    if (key === "sendMessage") {
      return Object.values(errorMsgs.sendMessage).some(Boolean);
    }
    return Boolean(errorMsgs[key]);
  };

  const validateForms = (): boolean => {
    let hasValidationErrors = false;
    actionKeys.forEach((key) => {
      if (validateTabByKey(key)) {
        hasValidationErrors = true;
      }
    });
    return !hasValidationErrors;
  };

  const submitAllActions = () => {
    const submitPromises: Array<Promise<unknown>> = [];
    const hasPassedFormValidation = validateForms();
    if (!hasPassedFormValidation) return;
    setDisableSubmitControls(true);
    actionKeys.forEach((key) => {
      if (switches[key]) {
        submitPromises.push(submitTabByKey(key));
      }
    });
    Promise.allSettled(submitPromises)
      .then((results) => {
        refetchHandles.activityFeed();
        const hasRejected = results.some(
          (result) => result.status === "rejected",
        );
        openSnackbar({
          message: hasRejected ? "Completed with some errors" : "Success",
          status: hasRejected ? "warning" : "success",
        });
      })
      .catch((err) => {
        logger.sendError(err);
      })
      .finally(resetAllState);
  };

  const clearKeyErrors = useCallback((key: ActionKey) => {
    setTabErrors((prev) => ({ ...prev, [key]: false }));
    switch (key) {
      case "sendMessage":
        setErrorMsgs((prev) => ({ ...prev, sendMessage: newEmptyChannelErrors() }));
        setFieldErrors((prev) => ({ ...prev, sendMessage: { emailSubject: "", smsUseCase: "" } }));
        break;
      case "logCall":
        setErrorMsgs((prev) => ({ ...prev, logCall: "" }));
        setFieldErrors((prev) => ({ ...prev, logCall: { outcome: "" } }));
        break;
      case "addNote":
        setErrorMsgs((prev) => ({ ...prev, addNote: "" }));
        setFieldErrors((prev) => ({ ...prev, addNote: { note: "" } }));
        break;
      default:
        setErrorMsgs((prev) => ({ ...prev, changeStatus: "" }));
        setFieldErrors((prev) => ({ ...prev, changeStatus: { status: "", reason: "" } }));
    }
  }, []);

  const onSwitchToggle = useCallback((key: ActionKey) => {
    setSwitches((prev) => {
      const isEnabled = !prev[key];
      if (!isEnabled) {
        clearKeyErrors(key);
      }
      return {
        ...prev,
        [key]: isEnabled,
      };
    });
  }, [clearKeyErrors]);
  const onTabClick = useCallback((key: ActionKey) => {
    setActiveAction(tabActions[key]);
  }, []);

  // Change handlers for child forms
  const onSwitchChange = (
    stateValues: StateValues,
    key: ActionKey,
  ) => {
    setSwitches((prev) => ({ ...prev, [key]: true }));
    const shouldRevalidate =
      tabErrors[key] ||
      hasFieldErrorsByKey(key) ||
      hasMessagesByKey(key);
    setFormValues((prev) => {
      const nextValues = { ...prev, [key]: stateValues } as FormValues;
      if (shouldRevalidate) {
        validateTabByKey(key, nextValues, true);
      }
      return nextValues;
    });
  };

  const tabErrorTooltips = useMemo<Record<ActionKey, string>>(() => ({
    logCall:
      fieldErrors.logCall.outcome ||
      errorMsgs.logCall ||
      "Please resolve errors on this tab.",
    sendMessage:
      fieldErrors.sendMessage.emailSubject ||
      fieldErrors.sendMessage.smsUseCase ||
      errorMsgs.sendMessage.form ||
      errorMsgs.sendMessage.email ||
      errorMsgs.sendMessage.sms ||
      errorMsgs.sendMessage.letter ||
      "Please resolve errors on this tab.",
    addNote:
      fieldErrors.addNote.note ||
      errorMsgs.addNote ||
      "Please resolve errors on this tab.",
    changeStatus:
      fieldErrors.changeStatus.status ||
      fieldErrors.changeStatus.reason ||
      errorMsgs.changeStatus ||
      "Please resolve errors on this tab.",
  }), [fieldErrors, errorMsgs]);

  return {
    // state
    activeAction,
    switches,
    tabErrors,
    tabErrorTooltips,
    errorMsgs,
    fieldErrors,
    disableSubmitControls,
    changeStatusState: formValues.changeStatus,
    // handlers
    resetAllState,
    onSwitchToggle,
    onTabClick,
    onSwitchChange,
    submitAllActions,
  } as const;
};
